/**
 * PersonalityQuiz - [영혼의 성소] 12세 성인식 8문 8답 성격 시험 & 8대 파라미터 엔진
 * 
 * [세계관 설정]
 * - 솔라리스 행성은 4개의 태양과 2개의 달이 공전하는 4중 항성계 행성이다.
 * - 4태양이 본래의 천체 정렬 위치로 돌아오려면 10번의 계절 순환(현실 누적 플레이타임 1년 = 인게임 10계절)을 거쳐야 공식 1살을 먹는다.
 * - 솔라리스 왕국의 모든 아이는 12살이 되면 성인으로서 사회생활/모험을 시작하며,
 *   [영혼의 성소]에서 고위 사제의 8대 영혼 문답 시험을 치르고 자신의 타고난 성향과 운명을 받아들인다.
 */

import { PersonalityAxes, PersonalityProfile } from '../../core/Types';

export interface QuizOption {
  text: string;
  score: number; // +80 (극단 긍정), +30 (온건 긍정), -30 (온건 부정), -80 (극단 부정)
  secondaryDeltas?: Partial<PersonalityAxes>;
}

export interface PersonalityQuestion {
  id: number;
  axis: keyof PersonalityAxes;
  axisNameKr: string;
  question: string;
  options: [QuizOption, QuizOption, QuizOption, QuizOption];
}

export const PERSONALITY_QUESTIONS: PersonalityQuestion[] = [
  // Q1: 용기 (courage: -100 겁 ↔ +100 용기)
  {
    id: 1,
    axis: 'courage',
    axisNameKr: '용기 (Courage)',
    question: '어두운 폐허 깊은 곳, 거대한 마수가 굶주린 눈으로 그대를 노려볼 때 그대는 검을 어떻게 쥐겠는가?',
    options: [
      { text: '포효하며 먼저 돌진해 적의 심장을 겨눈다!', score: 80, secondaryDeltas: { moral: 10 } },
      { text: '방패를 들고 약점을 노릴 때까지 침착하게 버틴다.', score: 30, secondaryDeltas: { thinking: 10 } },
      { text: '동료가 올 때까지 거리를 벌리며 유인한다.', score: -30, secondaryDeltas: { extraversion: 10 } },
      { text: '어둠 속으로 숨죽여 조용히 도망친다.', score: -80, secondaryDeltas: { order: -10 } },
    ],
  },

  // Q2: 질서 (order: -100 혼돈 ↔ +100 질서)
  {
    id: 2,
    axis: 'order',
    axisNameKr: '질서 (Order)',
    question: '친구가 굶주린 가족을 위해 영주의 곡물 창고를 털다 붙잡혔다. 사제인 내게 판결을 청한다면?',
    options: [
      { text: '법은 만인에게 평등하다. 규율대로 엄격히 처벌해야 한다.', score: 80, secondaryDeltas: { thinking: 15 } },
      { text: '훔친 곡물을 변상할 노동을 시켜 죗값을 치르게 한다.', score: 30, secondaryDeltas: { moral: 10 } },
      { text: '딱한 사정을 감안해 이번 한 번만 경고하고 눈감아준다.', score: -30, secondaryDeltas: { moral: 15 } },
      { text: '영주가 부패했다! 감옥 문을 부수고 친구를 탈출시킨다!', score: -80, secondaryDeltas: { courage: 20 } },
    ],
  },

  // Q3: 선악 (moral: -100 악 ↔ +100 선)
  {
    id: 3,
    axis: 'moral',
    axisNameKr: '선악 (Moral)',
    question: '길가에 쓰러진 적국의 부상병이 목마름에 신음하며 손을 내밀고 있다. 그대의 손길은 어디로 향하는가?',
    options: [
      { text: '적인지 아군인지 불문하고 내 물통을 건네 상처를 치료한다.', score: 80, secondaryDeltas: { materialism: -15 } },
      { text: '최소한의 고통을 덜어주되 무기는 안전하게 압수한다.', score: 30, secondaryDeltas: { order: 10 } },
      { text: '남의 일에 휘말리기 싫으니 모른 척 지나쳐 내 갈 길을 간다.', score: -30, secondaryDeltas: { extraversion: -10 } },
      { text: '후환을 없애기 위해 즉시 차갑게 숨통을 끊는다.', score: -80, secondaryDeltas: { thinking: 15 } },
    ],
  },

  // Q4: 물질욕 (materialism: -100 금욕 ↔ +100 물질욕)
  {
    id: 4,
    axis: 'materialism',
    axisNameKr: '물질욕 (Materialism)',
    question: '던전 깊은 곳에서 태양의 황금 궤짝을 발견했다. 하지만 궤짝을 열면 유적의 고대 벽화가 무너져 내린다.',
    options: [
      { text: '보물 따윈 상관없다. 역사적인 유적 보존이 훨씬 중요하다.', score: -80, secondaryDeltas: { moral: 15 } },
      { text: '최대한 조심스럽게 꺼내되, 훼손 위험이 크면 미련 없이 포기한다.', score: -30, secondaryDeltas: { thinking: 10 } },
      { text: '약간의 파손은 감수하고 황금을 조심스레 챙긴다.', score: 30, secondaryDeltas: { courage: 10 } },
      { text: '유적이 부서지든 말든 황금 궤짝을 당장 부숴서 챙긴다!', score: 80, secondaryDeltas: { order: -15 } },
    ],
  },

  // Q5: 외향/내향 (extraversion: -100 I ↔ +100 E)
  {
    id: 5,
    axis: 'extraversion',
    axisNameKr: '외향성 (Extraversion)',
    question: '긴 원정을 마치고 솔라리움 광장에 축제가 열렸다. 승리를 축하하는 이 밤, 그대는 어디에 있는가?',
    options: [
      { text: '광장 한가운데서 모르는 사람들과 어깨동무하고 밤새 술잔을 부딪친다!', score: 80, secondaryDeltas: { perceiving: 15 } },
      { text: '동료들과 조용한 주점 구석 테이블에 모여 이야기꽃을 피운다.', score: 30, secondaryDeltas: { moral: 10 } },
      { text: '성벽 위 난간에 홀로 앉아 시원한 밤바람을 맞으며 사색한다.', score: -30, secondaryDeltas: { intuition: 10 } },
      { text: '아무도 없는 개인 숙소에 틀어박혀 혼자만의 평화로운 휴식을 취한다.', score: -80, secondaryDeltas: { perceiving: -15 } },
    ],
  },

  // Q6: 감각/직관 (intuition: -100 S ↔ +100 N)
  {
    id: 6,
    axis: 'intuition',
    axisNameKr: '직관성 (Intuition)',
    question: '미지의 안개 낀 미궁에 들어섰다. 갈림길에서 그대는 무엇을 신뢰하는가?',
    options: [
      { text: '발자국, 벽의 이끼, 바람의 방향 등 오직 눈앞의 구체적인 단서만 믿는다.', score: -80, secondaryDeltas: { thinking: 10 } },
      { text: '지도를 꼼꼼히 대조하며 안전성이 검증된 통로를 찾는다.', score: -30, secondaryDeltas: { order: 10 } },
      { text: '왠지 저쪽에서 마나의 부름이 느껴진다. 영감과 분위기를 따른다.', score: 30, secondaryDeltas: { courage: 10 } },
      { text: '나의 타고난 직관과 운명을 믿고 발길 닿는 대로 나아간다!', score: 80, secondaryDeltas: { perceiving: 15 } },
    ],
  },

  // Q7: 사고/감정 (thinking: -100 F ↔ +100 T)
  {
    id: 7,
    axis: 'thinking',
    axisNameKr: '사고/감정 (Thinking)',
    question: '동료가 작전 중 감정에 휩쓸려 치명적인 실수를 저질렀다. 전투가 끝난 뒤 그대가 건넬 첫마디는?',
    options: [
      { text: '"네 감정 때문에 파티가 전멸할 뻔했어. 다음엔 규칙대로 냉정히 움직여."', score: 80, secondaryDeltas: { order: 15 } },
      { text: '"결과적으로 매우 위험했으니, 다음 작전 때는 역할을 냉정히 재배치하자."', score: 30, secondaryDeltas: { perceiving: -10 } },
      { text: '"많이 놀랐지? 다친 데는 없어? 네 마음은 충분히 이해해."', score: -30, secondaryDeltas: { moral: 15 } },
      { text: '동료를 따뜻하게 안아주며 "네가 무사해서 정말 다행이야"라며 함께 눈물짓는다.', score: -80, secondaryDeltas: { extraversion: 15 } },
    ],
  },

  // Q8: 판단/인식 (perceiving: -100 J ↔ +100 P)
  {
    id: 8,
    axis: 'perceiving',
    axisNameKr: '즉흥성 (Perceiving)',
    question: '미지의 대륙 원정을 떠나기 전날 밤, 그대의 짐가방과 계획표는 어떤 상태인가?',
    options: [
      { text: '1일 차부터 10일 차까지 이동 경로와 비상약품 목록이 완벽히 정리되어 있다.', score: -80, secondaryDeltas: { order: 15 } },
      { text: '대략적인 이동 경로와 필수 장비 체크리스트를 꼼꼼히 챙겨두었다.', score: -30, secondaryDeltas: { thinking: 10 } },
      { text: '꼭 필요한 무기만 대충 챙기고, 나머지는 현지에서 조달한다.', score: 30, secondaryDeltas: { intuition: 10 } },
      { text: '가방도 안 쌌다! 발길 닿는 대로 즉흥적으로 떠나는 게 진짜 모험이지!', score: 80, secondaryDeltas: { courage: 15 } },
    ],
  },
];

/**
 * Clamp a number to [-100, 100]
 */
function clamp100(val: number): number {
  return Math.max(-100, Math.min(100, Math.round(val)));
}

/**
 * Resolve AD&D 9-Alignment from order and moral
 */
export function resolveAlignment(order: number, moral: number): { key: string; nameKr: string } {
  const oLabel = order >= 25 ? '질서' : order <= -25 ? '혼돈' : '중립';
  const mLabel = moral >= 25 ? '선' : moral <= -25 ? '악' : '중립';

  if (oLabel === '중립' && mLabel === '중립') {
    return { key: 'TN', nameKr: '진정한 중립 (True Neutral)' };
  }

  const oKey = order >= 25 ? 'L' : order <= -25 ? 'C' : 'N';
  const mKey = moral >= 25 ? 'G' : moral <= -25 ? 'E' : 'N';

  const fullNames: Record<string, string> = {
    LG: '질서 선 (Lawful Good)',
    NG: '중립 선 (Neutral Good)',
    CG: '혼돈 선 (Chaotic Good)',
    LN: '질서 중립 (Lawful Neutral)',
    TN: '진정한 중립 (True Neutral)',
    CN: '혼돈 중립 (Chaotic Neutral)',
    LE: '질서 악 (Lawful Evil)',
    NE: '중립 악 (Neutral Evil)',
    CE: '혼돈 악 (Chaotic Evil)',
  };

  const code = oKey + mKey;
  return { key: code, nameKr: fullNames[code] || `${oLabel} ${mLabel}` };
}

/**
 * Resolve MBTI 4-letter code from axes
 */
export function resolveMbti(axes: PersonalityAxes): string {
  const e = axes.extraversion >= 0 ? 'E' : 'I';
  const n = axes.intuition >= 0 ? 'N' : 'S';
  const t = axes.thinking >= 0 ? 'T' : 'F';
  const p = axes.perceiving >= 0 ? 'P' : 'J';
  return `${e}${n}${t}${p}`;
}

/**
 * Poetic Soul Titles derived from Alignment and MBTI
 */
const SOUL_TITLE_MAP: Record<string, string> = {
  // Good alignments
  'LG-ENFJ': '새벽을 밝히는 성기사 (Paladin of Dawn)',
  'LG-INFJ': '성소의 은둔 예언자 (Seer of the Sanctuary)',
  'LG-ESTJ': '철벽의 규율 기사단장 (Iron Commander)',
  'LG-ISTJ': '침묵의 충직한 수호자 (Silent Guardian)',
  'NG-INFP': '숲과 별의 치유사 (Starlight Healer)',
  'NG-ENFP': '희망을 전하는 방랑자 (Herald of Hope)',
  'NG-ISFJ': '등불을 밝히는 간호자 (Keeper of the Lantern)',
  'NG-ESFJ': '모닥불의 온화한 벗 (Warm Companion)',
  'CG-ENFP': '자유를 노래하는 음유시인 (Wild Minstrel)',
  'CG-INFP': '바람을 좇는 몽상가 (Wind Dreamer)',
  'CG-ENTP': '기발한 비전 책략가 (Arcane Trickster)',
  'CG-INTP': '비전을 탐구하는 은둔자 (Mystic Seeker)',

  // Neutral alignments
  'LN-ISTJ': '엄정한 율법의 심판관 (Law Arbiter)',
  'LN-ESTJ': '강철의 전술 지휘관 (Tactical Marshal)',
  'LN-INTJ': '장막 뒤의 체스마스터 (Grand Strategist)',
  'TN-ISTP': '묵묵한 야생의 생존자 (Wilderness Survivor)',
  'TN-INTP': '고대의 진리를 낚는 학자 (Lore Archivist)',
  'TN-ISFP': '자연과 호흡하는 방랑객 (Nature Drifter)',
  'CN-ENTP': '예측불허의 풍운아 (Agent of Chaos)',
  'CN-ESTP': '짜릿함을 좇는 모험광 (Thrill Seeker)',
  'CN-ESFP': '축제의 즉흥 검객 (Festival Blade)',

  // Dark / Pragmatic alignments
  'LE-INTJ': '냉철한 그림자 군주 (Shadow Sovereign)',
  'NE-ISTP': '냉혹한 암야의 암살자 (Night Assassin)',
  'CE-ESTP': '광란의 파괴투사 (Berserk Gladiator)',
};

export function resolveSoulTitle(alignmentKey: string, mbti: string): string {
  const key = `${alignmentKey}-${mbti}`;
  if (SOUL_TITLE_MAP[key]) return SOUL_TITLE_MAP[key];

  // Fallback evocative titles based on dominant traits
  if (alignmentKey.includes('G')) return `자비로운 ${mbti} 모험가`;
  if (alignmentKey.includes('L')) return `엄격한 ${mbti} 수호자`;
  if (alignmentKey.includes('C')) return `자유로운 ${mbti} 방랑자`;
  return `고독한 ${mbti} 탐색자`;
}

/**
 * Compute Complete Personality Profile from 8 Quiz Choices
 * @param selectedIndices Array of 8 chosen option indices (0, 1, 2, or 3)
 */
export function computePersonalityProfile(selectedIndices: number[]): PersonalityProfile {
  const axes: PersonalityAxes = {
    courage: 0,
    order: 0,
    moral: 0,
    materialism: 0,
    extraversion: 0,
    intuition: 0,
    thinking: 0,
    perceiving: 0,
  };

  for (let i = 0; i < PERSONALITY_QUESTIONS.length; i++) {
    const q = PERSONALITY_QUESTIONS[i];
    const optIdx = selectedIndices[i] ?? 0;
    const opt = q.options[Math.max(0, Math.min(3, optIdx))];

    // Apply primary axis score
    axes[q.axis] += opt.score;

    // Apply secondary cross-axis deltas
    if (opt.secondaryDeltas) {
      for (const [subAxis, delta] of Object.entries(opt.secondaryDeltas)) {
        if (delta !== undefined && subAxis in axes) {
          axes[subAxis as keyof PersonalityAxes] += delta;
        }
      }
    }
  }

  // Clamp all axes to [-100, 100]
  for (const k of Object.keys(axes) as Array<keyof PersonalityAxes>) {
    axes[k] = clamp100(axes[k]);
  }

  const align = resolveAlignment(axes.order, axes.moral);
  const mbti = resolveMbti(axes);
  const soulTitle = resolveSoulTitle(align.key, mbti);

  const summary = `4개의 태양이 지켜보는 [영혼의 성소]에서 각인된 운명: ${align.nameKr} 성향의 ${mbti} 영혼입니다.`;

  return {
    axes,
    alignment: align.nameKr,
    mbti,
    soulTitle,
    summary,
  };
}

/**
 * Real-Time Lifestyle Personality Drift (행동에 따른 성격의 유기적 변화)
 */
export type PersonalityActionType =
  | 'flee_combat'       // 전투 도주 (용기 감소)
  | 'protect_ally'      // 아군 보호/희생 (용기 및 선함 증가)
  | 'share_loot'        // 전리품 양보 (물질욕 감소, 선함 증가)
  | 'hoard_loot'        // 전리품 독점 (물질욕 증가)
  | 'break_law'         // 법/규칙 위반 (질서 감소)
  | 'uphold_law'        // 법/규칙 준수 (질서 증가)
  | 'tavern_fellowship' // 주점 연회/담소 (외향 증가)
  | 'solitary_meditate' // 고독한 명상/독서 (내향 및 직관 증가)
  | 'meticulous_plan'   // 꼼꼼한 사전 정비 (판단 J 증가)
  | 'impromptu_explore';// 즉흥 탐험 (인식 P 증가)

export function applyPersonalityDrift(
  axes: PersonalityAxes,
  action: PersonalityActionType,
  intensity: number = 1
): PersonalityAxes {
  const next = { ...axes };
  const d = Math.max(1, intensity);

  switch (action) {
    case 'flee_combat':
      next.courage = clamp100(next.courage - 2 * d);
      break;
    case 'protect_ally':
      next.courage = clamp100(next.courage + 1 * d);
      next.moral = clamp100(next.moral + 2 * d);
      break;
    case 'share_loot':
      next.materialism = clamp100(next.materialism - 2 * d);
      next.moral = clamp100(next.moral + 1 * d);
      break;
    case 'hoard_loot':
      next.materialism = clamp100(next.materialism + 2 * d);
      next.moral = clamp100(next.moral - 1 * d);
      break;
    case 'break_law':
      next.order = clamp100(next.order - 2 * d);
      break;
    case 'uphold_law':
      next.order = clamp100(next.order + 2 * d);
      break;
    case 'tavern_fellowship':
      next.extraversion = clamp100(next.extraversion + 2 * d);
      break;
    case 'solitary_meditate':
      next.extraversion = clamp100(next.extraversion - 2 * d);
      next.intuition = clamp100(next.intuition + 1 * d);
      break;
    case 'meticulous_plan':
      next.perceiving = clamp100(next.perceiving - 2 * d); // More J
      break;
    case 'impromptu_explore':
      next.perceiving = clamp100(next.perceiving + 2 * d); // More P
      break;
  }

  return next;
}
